/* eslint-disable jsx-a11y/no-static-element-interactions */
import Button from '@/components/Button';
import Container from '@/components/Container';
import Divider from '@/components/Divider';
import PageHeader from '@/components/PageHeader';
import {
  Enum_Completedtask_Type,
  Enum_Componentcommonembedded_Provider,
  Enum_Meditation_Type,
  MeditationDetailPage,
  MeditationEntity,
  UploadFileEntityResponse,
} from '@/graphql/generated/graphql';
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { completeTask } from '@/services/daily/complete-tasks.service';
import DefaultContentTemplateEmbedded from '@/components/DefaultContentTemplate/Embedded';

import { PauseIcon, PlayIcon, StopIcon } from '@heroicons/react/20/solid';
import { useParams } from 'react-router-dom';
import { fetchMeditation, fetchMeditationDetailPage } from '@/services/meditation/meditation.service';
import { CalmBreathingAnimation } from '@/pages/mindful-minute/BreathingWheel';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import Card from '@/components/Card';
import DefaultContentTemplateTags from '@/components/DefaultContentTemplate/Tags';
import { clearMeditationDataLayer, trackMeditation } from '@/lib/ga-custom-events/meditation';

export function renderMeditationCards({
  title,
  data,
  showDivider = true,
}: {
  title: string;
  data: MeditationEntity[];
  showDivider?: boolean;
  viewAll?: boolean;
  viewAllText?: string;
}) {
  if (!data?.length) {
    return null;
  }

  return (
    <AnimatedFadeInComponent>
      <div key={title}>
        <div className="flex items-center justify-between">
          <h3 className="mb-sh-15 font-larken text-sh-md">{title}</h3>
        </div>
        <div className="grid grid-cols-1 gap-sh-30 md:grid-cols-2 md:gap-4">
          {data?.map((meditation: MeditationEntity) => {
            return (
              <Card
                noImage
                key={meditation.id}
                title={meditation.attributes?.title || ''}
                description={meditation.attributes?.briefDescription || ''}
                author={meditation?.attributes?.presentedBy || ''}
                link={`/mindful-exercise/${meditation.attributes?.slug}`}
                tags={
                  <DefaultContentTemplateTags
                    tags={[meditation?.attributes?.mindful_exercise_type?.data?.attributes?.name || '']}
                    className="!mb-2 !mt-0"
                    duration={meditation?.attributes?.duration || undefined}
                  />
                }
              />
            );
          })}
        </div>
      </div>
      {showDivider ? <Divider /> : null}
    </AnimatedFadeInComponent>
  );
}

type RenderAudioProps = {
  meditation: MeditationEntity;
  setIsAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  isAnimating: boolean;
  page: MeditationDetailPage | null;
  duration: number | null;
  setIsFinished: React.Dispatch<React.SetStateAction<boolean>>;
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  isStarted: boolean;
  setIsStarted: (value: boolean) => void;
  key: number;
  setKey: React.Dispatch<React.SetStateAction<number>>;
  isFinished: boolean;
};

type RenderVideoProps = {
  meditation: MeditationEntity;
  video?: UploadFileEntityResponse | null;
  setIsAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  setDuration: React.Dispatch<React.SetStateAction<number | null>>;
  setIsStarted: (value: boolean) => void;
  isStarted: boolean;
  page: MeditationDetailPage | null;
  setIsFinished: React.Dispatch<React.SetStateAction<boolean>>;
  isFinished: boolean;
};

const renderAudio = ({
  meditation,
  setIsAnimating,
  isAnimating,
  page,
  duration,
  setIsFinished,
  isFinished,
  audioRef,
  isStarted,
  setIsStarted,
  key,
  setKey,
}: RenderAudioProps) => {
  const finished = isFinished && !isStarted;

  return (
    <>
      <div className="mb-20 grid grid-cols-1 xl:grid-cols-2">
        <div className="flex items-center justify-center px-5">
          <div className="flex flex-col items-center text-center">
            <>
              {meditation?.attributes?.title && !finished && (
                <h2 className="mb-sh-15 font-larken text-sh-md">{meditation?.attributes?.title}</h2>
              )}
              {page?.thankYou && finished ? (
                <h2 className="mb-sh-15 font-larken text-sh-md">{page?.thankYou}</h2>
              ) : null}
              <div className="text-centertext-sh-14-18">{meditation?.attributes?.briefDescription}</div>
              <div className="mt-sh-30 flex gap-sh-15">
                {isStarted && (
                  <>
                    <Button
                      onClick={() => {
                        if (isAnimating) {
                          audioRef?.current?.pause();
                        } else {
                          audioRef?.current?.play();
                        }
                        setIsAnimating(!isAnimating);
                      }}
                      theme={'solid'}
                      className={classNames('flex items-center justify-center transition duration-300 ease-in', {})}>
                      {isAnimating ? (
                        <PauseIcon className="inline-block h-4 w-4" />
                      ) : (
                        <PlayIcon className="inline-block h-4 w-4" />
                      )}
                      {isAnimating ? `${page?.pauseBtn || 'Pause'}` : `${page?.startBtn || 'Play'}`}
                    </Button>
                    <Button
                      onClick={() => {
                        setIsAnimating(false);
                        setKey((prevKey: number) => prevKey + 1);
                      }}
                      theme={'solid'}
                      className={classNames('flex items-center justify-center transition duration-300 ease-in', {})}>
                      <StopIcon className="inline-block h-4 w-4" />
                      {page?.stopBtn || 'Stop'}
                    </Button>
                  </>
                )}

                {!isStarted && !isFinished && (
                  <Button
                    onClick={() => {
                      setIsStarted(true);
                      audioRef?.current?.play();
                      setIsAnimating(true);
                    }}
                    disabled={isAnimating}
                    theme={'solid'}
                    className={classNames('flex items-center justify-center transition duration-300 ease-in', {})}>
                    {page?.startBtn || 'Start exercise'}
                  </Button>
                )}

                {finished && (
                  <Button
                    onClick={() => {
                      setIsStarted(true);
                      setKey((prevKey: number) => prevKey + 1);
                    }}
                    disabled={isAnimating}
                    theme={'solid'}
                    className={classNames('flex items-center justify-center transition duration-300 ease-in', {})}>
                    {page?.thankYou || 'Try the exercise again'}
                  </Button>
                )}
              </div>
            </>
          </div>
        </div>
        <div className="mt-10 flex items-center justify-center lg:mt-0">
          {duration ? (
            <CalmBreathingAnimation
              key={key}
              totalTime={duration}
              isAnimating={isAnimating}
              onFinish={async () => {
                setIsFinished(true);
                setIsAnimating(false);
                await completeTask({
                  entity: meditation,
                  type: Enum_Completedtask_Type.Meditation,
                });
              }}
            />
          ) : null}
        </div>
      </div>
    </>
  );
};

const renderVideo = ({
  meditation,
  video,
  setIsAnimating,
  setIsFinished,
  setDuration,
  setIsStarted,
  isStarted,
}: RenderVideoProps) => {
  return (
    <>
      <div className="grid grid-cols-1">
        <div className="flex items-center justify-center">
          <div className="relative flex w-full flex-col items-center">
            {video?.data?.attributes && (
              <DefaultContentTemplateEmbedded
                className="mindful-video !my-0"
                component={{
                  id: 'DefaultContentTemplateEmbedded',
                  link: video?.data?.attributes?.url,
                  provider: Enum_Componentcommonembedded_Provider.Youtube,
                }}
                onVideoPlay={() => {
                  if (!isStarted) {
                    setIsStarted(true);
                  }
                }}
                onDuration={(n) => {
                  setDuration(n);
                }}
                onEnded={async () => {
                  setIsFinished(true);
                  setIsAnimating(false);
                  await completeTask({
                    entity: meditation,
                    type: Enum_Completedtask_Type.Meditation,
                  });
                }}
              />
            )}

            <div className="w-full">
              {meditation?.attributes?.title ? (
                <h1 className="mt-sh-30 font-larken text-sh-36-48">{meditation?.attributes?.title}</h1>
              ) : null}

              <Divider />

              {meditation?.attributes?.briefDescription ? (
                <p className="my-sh-30 text-sh-xxs">{meditation?.attributes?.briefDescription}</p>
              ) : null}

              {meditation?.attributes?.presentedBy ? (
                <p className="text-sh-xxs text-sh-grey-4e">
                  By <strong>{meditation?.attributes?.presentedBy}</strong>
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default function MeditationDetailUIPage() {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [page, setPage] = useState<MeditationDetailPage | null>(null);
  const [meditation, setMeditation] = useState<MeditationEntity | null>(null);
  const { slug } = useParams();

  const [isStarted, setIsStartedState] = useState(false);
  const [key, setKey] = React.useState(0);

  const { audio, video } = meditation?.attributes || {};
  const type = meditation?.attributes?.type;

  const [duration, setDuration] = useState<number | null>(null);

  const audioFile = audio?.audio?.data?.attributes?.url || '';
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const setIsStarted = (state: boolean) => {
    setIsStartedState(state);
  };

  useEffect(() => {
    async function init() {
      if (!slug) {
        return;
      }

      const page = await fetchMeditationDetailPage();
      setPage(page?.page);

      const result = await fetchMeditation(slug);

      if (!result?.meditation) {
        return;
      }

      setMeditation(result?.meditation);
    }

    init();

    return () => {
      clearMeditationDataLayer();
    };
  }, [slug]);

  useEffect(() => {
    if (type !== Enum_Meditation_Type.Video && (!audioFile || !audioRef.current)) {
      return;
    }

    if (isAnimating) {
      audioRef?.current?.play();
    }

    if (isFinished) {
      trackMeditation({
        meditation_id: meditation?.id || '',
        meditation_name: meditation?.attributes?.title || '',
      });
      audioRef?.current?.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnimating, audioFile, audioRef, isFinished]);

  useEffect(() => {
    if (audioRef?.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [key, audioRef]);

  useEffect(() => {
    if (type === Enum_Meditation_Type.Audio && audioFile) {
      audioRef.current = new Audio(audioFile);
      audioRef.current.onloadedmetadata = () => {
        setDuration(audioRef.current?.duration ?? null);
      };
    }
  }, [type, audioFile]);

  const renderContent = () => {
    return meditation?.id ? (
      <div className="col-span-12 mt-sh-15 gap-4 lg:col-start-3 lg:col-end-11">
        <div>
          {type === Enum_Meditation_Type.Audio
            ? renderAudio({
                meditation,
                setIsAnimating,
                isAnimating,
                page,
                duration,
                setIsFinished,
                audioRef,
                isStarted,
                setIsStarted,
                key,
                setKey,
                isFinished,
              })
            : null}
          {type === Enum_Meditation_Type.Video && video
            ? renderVideo({
                meditation,
                video,
                setIsAnimating,
                setDuration,
                isStarted,
                setIsStarted,
                page,
                setIsFinished,
                isFinished,
              })
            : null}
        </div>
      </div>
    ) : null;
  };

  return (
    <div className="min-h-[46rem]">
      <Container className="mt-sh-60">
        <div className="col-span-12 gap-4 lg:col-start-3 lg:col-end-11">
          <PageHeader title="Mindful exercise" url="/mindful-exercise" type="none" />
        </div>
        {renderContent()}

        {(meditation?.attributes?.relatedContent?.data?.length || 0) > 0 && (
          <div className="col-span-12 gap-4 lg:col-start-3 lg:col-end-11">
            <Divider />

            <div className="mt-sh-30">
              {renderMeditationCards({
                title: page?.youMayAlsoLikeTitle || 'You may also like',
                data: meditation?.attributes?.relatedContent?.data || [],
                showDivider: false,
              })}
            </div>
          </div>
        )}
      </Container>
    </div>
  );
}
